/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useEmployeesStore, withSelfOwner, type EmployeeRecord } from '../../team/store/useEmployeesStore';
import { plural } from '../../shared/format/plural';
import { formatMoney } from '../../shared/format/public-api';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField';
import { parseIsoDate } from '@shared/vueltoDateTime';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors'
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { TicketListRow } from '../../shared/ticket/TicketListRow'
import { TicketButton } from '../../shared/ticket/TicketButton'
import { TicketSheet } from '../../shared/ticket/TicketSheet'
import { ticketControls } from '../../shared/ticket/ticketControls.styles'
import { OperationalScrollScreen } from '../../shared/ticket/OperationalScrollScreen';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState'
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';
import { showSuccess, showError } from '../../shared/ui/appToast'
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { listBoothRentals, saveBoothRental, deleteBoothRental } from '../infra/commissionsRepo';

type BoothRental = {
  id: string;
  employeeUserId: string;
  employeeName: string;
  branchId: string | null;
  periodFrom: string;
  periodTo: string;
  amount: number;
  frequency: string;
  status: string;
  note: string | null;
  createdAt: string;
};

const FREQUENCIES = ['monthly', 'weekly', 'daily'] as const;
type Frequency = (typeof FREQUENCIES)[number];

const FREQUENCY_LABELS: Record<Frequency, string> = {
  monthly: 'Mensual',
  weekly: 'Semanal',
  daily: 'Diario',
};

/**
 * `period_from`/`period_to` son columnas `date` ("YYYY-MM-DD").
 * `new Date('2025-09-26')` parsea como UTC → en UTC-3 muestra el día anterior.
 * `parseIsoDate` construye fecha local — sin off-by-one.
 */
function formatDate(iso: string): string {
  if (!iso) return '';
  const datePart = /^(\d{4}-\d{2}-\d{2})/.exec(iso)?.[1];
  if (!datePart) {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }
  return parseIsoDate(datePart).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function BoothRentalsScreen() {
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const { canEdit, actions } = useBranchPermission('Commissions');
  const canManage = canEdit || actions.manage_plans === true;
  const { confirm, ConfirmModal } = useOperationalConfirm();
  const capabilityGate = useCapabilityGate('commissions', {
    title: 'Comisiones no disponibles',
    subtitle: 'Este rubro no tiene comisiones.',
  });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rentals, setRentals] = useState<BoothRental[]>([]);
  const [modal, setModal] = useState<{ visible: boolean; rental?: BoothRental }>({ visible: false });

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const rows = await listBoothRentals({ branchId: currentBranchId });
      setRentals(rows as BoothRental[]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar alquileres.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentBranchId]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  const handleSave = useCallback(async (params: {
    employeeUserId: string;
    periodFrom: string;
    periodTo: string;
    amount: number;
    frequency: Frequency;
    note: string | null;
  }) => {
    try {
      const result = await saveBoothRental({
        employeeUserId: params.employeeUserId,
        periodFrom: params.periodFrom,
        periodTo: params.periodTo,
        amount: params.amount,
        frequency: params.frequency,
        note: params.note,
        branchId: currentBranchId ?? null,
        id: modal.rental?.id ?? null,
      });
      if (result.ok) {
        showSuccess('Alquiler guardado', modal.rental ? 'Alquiler actualizado' : 'Alquiler creado');
        setModal({ visible: false });
        void load(true);
      } else {
        showError('Error', result.error ?? 'No se pudo guardar el alquiler.');
      }
    } catch (e: unknown) {
      showError('Error', e instanceof Error ? e.message : 'Error al guardar alquiler.');
    }
  }, [modal.rental, currentBranchId, load]);

  const handleDelete = useCallback(async (rental: BoothRental) => {
    const ok = await confirm({
      title: 'Eliminar alquiler',
      message: `¿Seguro que querés eliminar el alquiler de "${rental.employeeName}"?`,
      confirmLabel: 'Eliminar',
      destructive: true,
    });
    if (!ok) return;
    try {
      const result = await deleteBoothRental(rental.id);
      if (result.ok) {
        showSuccess('Eliminado', 'Alquiler eliminado');
        void load();
      } else {
        showError('Error', result.error ?? 'No se pudo eliminar.');
      }
    } catch (e: unknown) {
      showError('Error', e instanceof Error ? e.message : 'Error al eliminar.');
    }
  }, [confirm, load]);

  // ---- Loading / Error states ----
  if (!capabilityGate.allowed) {
    return <OperationalScrollScreen header={null} body={capabilityGate.blocked} />;
  }

  if (loading) {
    return (
      <OperationalScrollScreen
        header={null}
        body={<ModuleEmptyState loading kicker="Comisiones" />}
      />
    );
  }

  if (error) {
    return (
      <OperationalScrollScreen
        header={null}
        body={
          <ModuleEmptyState
            variant="nest"
            kicker="Comisiones"
            title="No se pudieron cargar los alquileres"
            subtitle={error}
            primaryAction={{ label: 'Reintentar', onPress: () => void load() }}
          />
        }
      />
    );
  }

  const header = (
    <TicketModuleHeader
      kicker="COMISIONES"
      title="Alquileres de cabina"
      meta={`${rentals.length} ${plural(rentals.length, 'alquiler', 'alquileres')}`}
      // Sin alquileres el CTA vive en el empty state — no duplicar «+ Alquiler».
      trailing={
        canManage && rentals.length > 0 ? (
          <TicketButton label="+ Alquiler" variant="gold" onPress={() => setModal({ visible: true })} />
        ) : null
      }
    />
  );

  const body = (
    <>
      {rentals.length === 0 ? (
        <ModuleEmptyState
          variant="nest"
          kicker="Comisiones"
          title="No hay alquileres de cabina"
          subtitle="Registrá el primer alquiler de cabina para empezar a gestionar los pagos periódicos de tus empleados."
          primaryAction={
            canManage
              ? { label: '+ Alquiler', onPress: () => setModal({ visible: true }) }
              : undefined
          }
        />
      ) : (
        <PaperSheet>
          {rentals.map((r, idx) => {
            const isLast = idx === rentals.length - 1;
            const freqLabel = FREQUENCY_LABELS[r.frequency as Frequency] ?? r.frequency;
            const isActive = r.status === 'active';
            return (
              <TicketListRow
                key={r.id}
                title={r.employeeName}
                subtitle={r.note ?? null}
                meta={`${formatDate(r.periodFrom)} → ${formatDate(r.periodTo)} · ${freqLabel}`}
                value={formatMoney(r.amount)}
                statusLabel={isActive ? 'Activo' : r.status}
                statusTone={isActive ? 'ok' : 'muted'}
                group={isLast ? 'groupedLast' : 'grouped'}
                footer={
                  canManage ? (
                    <View style={styles.cardActions}>
                      <TicketButton
                        label="Editar"
                        variant="ghost"
                        onPress={() => setModal({ visible: true, rental: r })}
                      />
                      <TicketButton
                        label="Eliminar"
                        variant="danger"
                        onPress={() => void handleDelete(r)}
                      />
                    </View>
                  ) : null
                }
              />
            );
          })}
        </PaperSheet>
      )}
    </>
  );

  const modals = (
    <>
      <BoothRentalModal
        visible={modal.visible}
        rental={modal.rental}
        onClose={() => setModal({ visible: false })}
        onSave={handleSave}
      />
      {ConfirmModal}
    </>
  );

  return (
    <OperationalScrollScreen
      header={header}
      body={body}
      modals={modals}
      refreshing={refreshing}
      onRefresh={() => void load(true)}
    />
  );
}

// ============================================================================
// Booth Rental Modal — create / edit
// ============================================================================
function BoothRentalModal({ visible, rental, onClose, onSave }: {
  visible: boolean;
  rental?: BoothRental;
  onClose: () => void;
  onSave: (params: {
    employeeUserId: string;
    periodFrom: string;
    periodTo: string;
    amount: number;
    frequency: Frequency;
    note: string | null;
  }) => Promise<void> | void;
}) {
  const employeesRaw = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const employees = useMemo(() => withSelfOwner(employeesRaw, selfOwner), [employeesRaw, selfOwner]);
  const loadEmployees = useEmployeesStore((s) => s.loadEmployees);
  const [employeeUserId, setEmployeeUserId] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState<Frequency>('monthly');
  const [note, setNote] = useState('');
  const [search, setSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setEmployeeUserId(rental?.employeeUserId ?? '');
    setEmployeeName(rental?.employeeName ?? '');
    setPeriodFrom(rental?.periodFrom ?? '');
    setPeriodTo(rental?.periodTo ?? '');
    setAmount(rental?.amount != null ? String(rental.amount) : '');
    setFrequency((rental?.frequency as Frequency) ?? 'monthly');
    setNote(rental?.note ?? '');
    setSearch('');
    setPickerOpen(false);
    setLoadingEmps(true);
    void loadEmployees().finally(() => setLoadingEmps(false));
  }, [visible, rental, loadEmployees]);

  const activeEmployees = useMemo(
    () => employees.filter((e) => e.isActive !== false && e.authUserId),
    [employees],
  );

  const filteredEmployees = useMemo(() => {
    if (!search.trim()) return activeEmployees;
    const q = search.trim().toLowerCase();
    return activeEmployees.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        (e.email ?? '').toLowerCase().includes(q),
    );
  }, [activeEmployees, search]);

  const handleSelectEmployee = useCallback((emp: EmployeeRecord) => {
    setEmployeeUserId(emp.authUserId ?? '');
    setEmployeeName(emp.name);
    setPickerOpen(false);
    setSearch('');
  }, []);

  const handleSave = useCallback(async () => {
    if (saving) return;
    if (!employeeUserId) {
      showError('Error', 'Seleccioná un empleado.');
      return;
    }
    if (!periodFrom.trim()) {
      showError('Error', 'Ingresá la fecha desde.');
      return;
    }
    if (!periodTo.trim()) {
      showError('Error', 'Ingresá la fecha hasta.');
      return;
    }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      showError('Error', 'Ingresá un monto válido.');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        employeeUserId,
        periodFrom: periodFrom.trim(),
        periodTo: periodTo.trim(),
        amount: amt,
        frequency,
        note: note.trim() || null,
      });
    } finally {
      setSaving(false);
    }
  }, [saving, employeeUserId, periodFrom, periodTo, amount, frequency, note, onSave]);

  return (
    <TicketSheet
      visible={visible}
      title={rental ? 'Editar alquiler' : 'Nuevo alquiler'}
      onClose={onClose}
      dialogStyle={{ maxWidth: 520, width: '90%', maxHeight: '90%' }}
      footer={
        <View style={styles.modalActions}>
          <TicketButton label="Cancelar" variant="ghost" onPress={onClose} style={{ flex: 1 }} />
          <TicketButton
            label={saving ? 'Guardando…' : 'Guardar'}
            variant="gold"
            onPress={handleSave}
            disabled={saving}
            style={{ flex: 1 }}
          />
        </View>
      }
    >
      <ScrollView
        style={styles.modalBodyScroll}
        contentContainerStyle={styles.modalBodyContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* ===== Empleado ===== */}
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Empleado</Text>
        </View>
        {pickerOpen ? (
          <View style={styles.fieldGroup}>
            <View style={styles.searchWrap}>
              <Ionicons name="search-outline" size={16} color={ticketColors.ink3} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                value={search}
                onChangeText={setSearch}
                placeholder="Buscar empleado…"
                placeholderTextColor={ticketColors.ink3}
                autoFocus
              />
              {search.length > 0 ? (
                <TouchableOpacity onPress={() => setSearch('')} style={styles.searchClear} hitSlop={8}>
                  <Ionicons name="close-circle" size={16} color={ticketColors.ink3} />
                </TouchableOpacity>
              ) : null}
            </View>
            {loadingEmps ? (
              <ActivityIndicator size="small" color={ticketColors.gold} style={{ paddingVertical: spacing.md }} />
            ) : filteredEmployees.length === 0 ? (
              <Text style={styles.emptySmall}>
                {activeEmployees.length === 0 ? 'No hay empleados activos.' : 'Sin resultados.'}
              </Text>
            ) : (
              <View style={styles.pickerList}>
                {filteredEmployees.slice(0, 50).map((emp) => (
                  <TouchableOpacity
                    key={emp.id}
                    style={[
                      styles.pickerRow,
                      employeeUserId === emp.authUserId && styles.pickerRowActive,
                    ]}
                    onPress={() => handleSelectEmployee(emp)}
                  >
                    <View style={styles.pickerRowInfo}>
                      <Text style={styles.pickerRowName}>{emp.name}</Text>
                      <Text style={styles.pickerRowMeta}>
                        {emp.email ?? 'Sin email'} · {emp.role}
                      </Text>
                    </View>
                    {employeeUserId === emp.authUserId ? (
                      <Ionicons name="checkmark-circle" size={18} color={ticketColors.gold} />
                    ) : null}
                  </TouchableOpacity>
                ))}
              </View>
            )}
            <TouchableOpacity onPress={() => setPickerOpen(false)} style={styles.cancelPickerBtn}>
              <Text style={styles.cancelPickerText}>Cancelar</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity
            style={styles.employeeSelectBtn}
            onPress={() => setPickerOpen(true)}
          >
            <View style={styles.employeeSelectInfo}>
              <Text style={styles.employeeSelectLabel}>Empleado</Text>
              <Text
                style={[
                  styles.employeeSelectValue,
                  !employeeName && styles.employeeSelectPlaceholder,
                ]}
                numberOfLines={1}
              >
                {employeeName || 'Seleccionar empleado…'}
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={16} color={ticketColors.ink3} />
          </TouchableOpacity>
        )}

        {/* ===== Período ===== */}
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Período</Text>
        </View>
        <View style={styles.dateRow}>
          <View style={styles.dateField}>
            <Text style={ticketControls.fieldLabel}>Desde</Text>
            <InventoryIsoDateField
              value={periodFrom}
              onChange={setPeriodFrom}
              emptyLabel="Elegí fecha"
              ariaLabel="Fecha desde"
              showClearButton={false}
              iosEmptyBehavior="openPicker"
            />
          </View>
          <View style={styles.dateField}>
            <Text style={ticketControls.fieldLabel}>Hasta</Text>
            <InventoryIsoDateField
              value={periodTo}
              onChange={setPeriodTo}
              emptyLabel="Elegí fecha"
              ariaLabel="Fecha hasta"
              showClearButton={false}
              iosEmptyBehavior="openPicker"
            />
          </View>
        </View>

        {/* ===== Monto y frecuencia ===== */}
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Monto y frecuencia</Text>
        </View>
        <View style={styles.fieldGroup}>
          <Text style={ticketControls.fieldLabel}>Monto</Text>
          <TextInput
            style={ticketControls.input}
            value={amount}
            onChangeText={setAmount}
            placeholder="0.00"
            placeholderTextColor={ticketColors.ink3}
            keyboardType="decimal-pad"
          />
        </View>
        <View style={styles.fieldGroup}>
          <Text style={ticketControls.fieldLabel}>Frecuencia</Text>
          <View style={styles.typeRow}>
            {FREQUENCIES.map((f) => (
              <TouchableOpacity
                key={f}
                style={[styles.typeBtn, frequency === f && styles.typeBtnActive]}
                onPress={() => setFrequency(f)}
              >
                <Text style={[styles.typeBtnText, frequency === f && styles.typeBtnTextActive]}>
                  {FREQUENCY_LABELS[f]}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* ===== Nota ===== */}
        <View style={styles.fieldGroup}>
          <Text style={ticketControls.fieldLabel}>
            Nota <Text style={styles.fieldHint}>(opcional)</Text>
          </Text>
          <TextInput
            style={[ticketControls.input, styles.inputMultiline]}
            value={note}
            onChangeText={setNote}
            placeholder="Ej: Cabina 3, contrato verbal"
            placeholderTextColor={ticketColors.ink3}
            multiline
          />
        </View>
      </ScrollView>
    </TicketSheet>
  );
}

const styles = StyleSheet.create({
  cardActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingTop: spacing.sm,
    flexWrap: 'wrap',
  },
  modalActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    padding: spacing.md,
    paddingTop: 0,
  },
  modalBodyScroll: {
    flex: 1,
  },
  modalBodyContent: {
    padding: spacing.md,
    paddingBottom: spacing.lg,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginTop: spacing.md,
    marginBottom: spacing.sm,
    paddingBottom: spacing.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  fieldGroup: {
    marginBottom: spacing.sm,
  },
  fieldHint: {
    fontSize: 11,
    fontWeight: '400',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  inputMultiline: {
    height: 'auto',
    minHeight: 60,
    paddingVertical: spacing.sm,
  },
  dateRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  dateField: {
    flex: 1,
  },
  typeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  typeBtn: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: ticketColors.tile,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  typeBtnActive: {
    backgroundColor: ticketColors.brandSoft,
    borderColor: ticketColors.brandBorderSoft,
  },
  typeBtnText: {
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  typeBtnTextActive: {
    color: ticketColors.brandInk,
    fontWeight: '700',
  },
  emptySmall: {
    textAlign: 'center',
    color: ticketColors.ink3,
    fontSize: 13,
    paddingVertical: spacing.md,
    fontFamily: ticketFonts.sans,
  },
  // Employee picker
  employeeSelectBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    minHeight: 48,
  },
  employeeSelectInfo: {
    flex: 1,
    minWidth: 0,
  },
  employeeSelectLabel: {
    fontSize: 10,
    fontWeight: '600',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
    marginBottom: 2,
  },
  employeeSelectValue: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  employeeSelectPlaceholder: {
    fontWeight: '400',
    color: ticketColors.ink3,
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    marginBottom: spacing.sm,
    minHeight: 38,
  },
  searchIcon: {
    paddingLeft: 10,
  },
  searchInput: {
    flex: 1,
    borderWidth: 0,
    backgroundColor: 'transparent',
    fontSize: 14,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  searchClear: {
    paddingHorizontal: 10,
  },
  pickerList: {
    maxHeight: 220,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 8,
    backgroundColor: ticketColors.paper,
  },
  pickerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  pickerRowActive: {
    backgroundColor: ticketColors.brandSoft,
  },
  pickerRowInfo: {
    flex: 1,
    minWidth: 0,
  },
  pickerRowName: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  pickerRowMeta: {
    fontSize: 12,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
    marginTop: 2,
  },
  cancelPickerBtn: {
    alignSelf: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginTop: spacing.xs,
  },
  cancelPickerText: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
});
